<template>
  <span class="ai-usage-tag">
    <el-dropdown
      trigger="hover"
      :disabled="disabled || loading || fetching || isFetchedReadonly"
      placement="bottom-start"
      @command="onCommand"
    >
      <!-- 已标记：彩色标签（悬浮出现清除按钮）；未标记：淡色入口 -->
      <span
        v-if="hasTag"
        class="ai-tag-badge"
        :class="{ 'is-readonly': isFetchedReadonly }"
        :style="{ color: color, borderColor: color, background: bg }"
      >
        <i v-if="loading" class="el-icon-loading"></i>
        <template v-else>
          {{ label }}
          <i v-if="!isFetchedReadonly" class="el-icon-arrow-down ai-tag-arrow"></i>
          <i
            v-if="!isFetchedReadonly"
            class="el-icon-close ai-tag-clear"
            title="清除标记（改回未使用AI）"
            @click.stop="clear"
          ></i>
        </template>
      </span>
      <span v-else class="ai-tag-add">
        <i v-if="loading || fetching" class="el-icon-loading"></i>
        <template v-else>
          <i class="el-icon-plus"></i>AI标记
        </template>
      </span>

      <el-dropdown-menu slot="dropdown" class="ai-usage-menu">
        <li class="ai-menu-warning">
          <i class="el-icon-warning"></i>请慎重选择，标记之后将不能修改
        </li>
        <el-dropdown-item
          v-for="opt in options"
          :key="opt.value"
          :command="opt.value"
          :class="{ 'is-current': opt.value === innerValue }"
        >
          <div class="ai-opt">
            <span class="ai-opt-title">
              <i :class="opt.icon"></i>{{ opt.label }}
              <i v-if="opt.value === innerValue" class="el-icon-check ai-opt-check"></i>
            </span>
            <span class="ai-opt-desc">{{ opt.desc }}</span>
            <span class="ai-opt-sub">{{ opt.sub }}</span>
          </div>
        </el-dropdown-item>
        <li class="ai-menu-foot">
          片头钩子、核心卖点或主叙事由AI承担时，即使片段少也按"大量AI"。
        </li>
      </el-dropdown-menu>
    </el-dropdown>
  </span>
</template>

<script>
import { AI_USAGE_OPTIONS, getAiTagLabel, normalizeAiTag } from './constants';
import { postUpdateFileAiTag } from '../../serve/material';
import { getAiTag } from './aiTagQueue';

const COLOR_MAP = {
  [1]: '#f56c6c', // ALL (全部AI)
  [2]: '#409eff', // LITTLE
  [3]: '#e6a23c', // HEAVY
  [4]: '#67c23a', // UNUSED (未使用AI)
  [0]: '#909399', // UNCONFIRMED
};
const BG_MAP = {
  [1]: '#fef0f0',
  [2]: '#ecf5ff',
  [3]: '#fdf6ec',
  [4]: '#f0f9eb',
  [0]: '#f4f4f5',
};

export default {
  name: 'AiUsageTag',
  props: {
    // 素材 file_id，有值时选择即调接口
    fileId: {
      type: [String, Number],
      default: '',
    },
    /**
     * 当前标记值（0=未确认，1=全部AI，2=少量AI，3=大量AI，4=未使用AI）
     *
     * - undefined（不传）：自托管模式，组件根据 fileId 自动查询并展示
     * - null：受控模式，明确表示无标记
     * - 数字：受控模式，展示该标记
     */
    value: {
      type: [Number, String],
      default: undefined,
    },
    // 禁用状态（仅禁止修改，不影响自动查询）
    disabled: {
      type: Boolean,
      default: false,
    },
    // 是否在 fileId 变化时自动查询已有标记
    autoFetch: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      options: AI_USAGE_OPTIONS,
      loading: false,
      fetching: false,
      debounceTimer: null,
      // 自动查询到的标记值，仅在未传 value 时生效
      fetchedTag: null,
      // 接口返回的文案，用于前端未收录的枚举值兜底展示
      fetchedDesc: '',
      // 当前会话中是否已成功保存过标记（保存后立即锁定，无需刷新页面）
      hasSaved: false,
      isDestroyed: false,
    };
  },
  computed: {
    // 是否由父组件受控（显式传入 value，含 null）
    isControlled() {
      return this.value !== undefined;
    },
    // 统一转为数字，无标记时为 null（0=未确认 视同未标记）
    innerValue() {
      if (this.isControlled) {
        if (this.value === null || this.value === '' || this.value === 0 || this.value === '0') return null;
        return normalizeAiTag(this.value);
      }
      return this.fetchedTag;
    },
    hasTag() {
      return this.innerValue !== null;
    },
    label() {
      return getAiTagLabel(this.innerValue, this.fetchedDesc);
    },
    color() {
      return COLOR_MAP[this.innerValue] || '#909399';
    },
    bg() {
      return BG_MAP[this.innerValue] || '#f4f4f5';
    },
    // 自动查询到的标记为只读（不允许重复标记）
    // 或者当前会话中已成功保存过，也立即锁定
    isFetchedReadonly() {
      if (this.hasSaved) return true;
      return !this.isControlled && this.fetchedTag !== null;
    },
  },
  watch: {
    // fileId 变化时重新查询（换素材时组件可能不重建，必须重置状态避免残留上一个素材的标记）
    fileId: {
      immediate: true,
      handler(val, oldVal) {
        if (String(val || '') === String(oldVal || '')) return;
        this.fetchedTag = null;
        this.fetchedDesc = '';
        this.hasSaved = false;
        this.fetchAiTag();
      },
    },
  },
  beforeDestroy() {
    this.isDestroyed = true;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    /**
     * 查询已有的 AI 标记
     *
     * 仅在未传 value（自托管模式）且有 fileId 时执行。
     * disabled 状态下也会查询，这样视频处理完成后可直接展示已有标记。
     */
    async fetchAiTag() {
      if (!this.autoFetch || this.isControlled || !this.fileId) return;

      this.fetching = true;
      try {
        const { aiTag, aiTagDesc } = await getAiTag(this.fileId);
        if (this.isDestroyed) return;
        this.fetchedTag = aiTag;
        this.fetchedDesc = aiTagDesc;
        // 用独立事件通知，不复用 change（change 语义是用户主动修改，
        // 复用会让父组件把自动查询误判为表单变更）
        this.$emit('fetched', aiTag);
      } catch (e) {
        // 查询失败降级为「未标记」入口，用户可点击重新选择
        if (this.isDestroyed) return;
        this.fetchedTag = null;
        this.fetchedDesc = '';
      } finally {
        if (!this.isDestroyed) {
          this.fetching = false;
        }
      }
    },
    onCommand(command) {
      if (command === this.innerValue) return;
      this.doChange(command);
    },
    clear() {
      if (this.disabled || this.loading || !this.hasTag || this.isFetchedReadonly) return;
      this.doChange(null);
    },
    doChange(newVal) {
      // 防抖：300ms 内连续切换只以最后一次为准
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.saveTag(newVal);
      }, 300);
    },
    async saveTag(newVal) {
      const fileId = this.fileId;

      // 无 fileId 时仅做本地状态变更（上传中场景）
      if (!fileId) {
        this.syncLocalTag(newVal);
        this.$emit('change', newVal);
        return;
      }

      this.loading = true;
      try {
        const res = await postUpdateFileAiTag({
          fileIds: String(fileId),
          aiTag: newVal,
        });
        if (res && res.code === 0) {
          this.hasSaved = true;
          this.syncLocalTag(newVal);
          this.$emit('change', newVal);
        } else {
          this.$message && this.$message.error('AI标记保存失败');
          this.$emit('error', res);
        }
      } catch (err) {
        this.$message && this.$message.error('AI标记保存失败，请重试');
        this.$emit('error', err);
      } finally {
        this.loading = false;
      }
    },
    /**
     * 同步自托管模式下的本地标记值
     * 受控模式由父组件负责回写 value，这里不处理
     */
    syncLocalTag(val) {
      if (this.isControlled) return;
      this.fetchedTag = val;
      this.fetchedDesc = '';
    },
  },
};
</script>

<style scoped lang="scss">
.ai-usage-tag {
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
}

.ai-tag-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 6px;
  height: 20px;
  line-height: 18px;
  font-size: 12px;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;

  &.is-readonly {
    cursor: default;
  }

  i {
    margin-left: 2px;
    font-size: 12px;
  }

  .ai-tag-clear {
    display: none;
  }

  &:hover {
    .ai-tag-arrow {
      display: none;
    }

    .ai-tag-clear {
      display: inline-block;

      &:hover {
        color: #f56c6c;
      }
    }
  }
}

.ai-tag-add {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
  cursor: pointer;

  i {
    margin-right: 2px;
  }

  &:hover {
    color: #409eff;
  }
}
</style>

<!-- 下拉菜单样式（menu 挂载在 body 上，不能用 scoped） -->
<style lang="scss">
.ai-usage-menu {
  max-width: 300px;

  .el-dropdown-menu__item {
    line-height: 1.4;
    padding: 8px 16px;
    height: auto;

    &.is-current {
      background: #f5f9ff;
    }
  }

  .ai-opt {
    display: flex;
    flex-direction: column;

    .ai-opt-title {
      display: flex;
      align-items: center;
      font-size: 13px;
      font-weight: 600;
      color: #303133;

      i {
        margin-right: 4px;
        color: #409eff;
      }

      .ai-opt-check {
        margin-left: 6px;
        color: #67c23a;
      }
    }

    .ai-opt-desc {
      margin-top: 2px;
      font-size: 12px;
      color: #606266;
      white-space: normal;
    }

    .ai-opt-sub {
      margin-top: 1px;
      font-size: 12px;
      color: #a8abb2;
      white-space: normal;
    }
  }

  .ai-menu-foot {
    margin-top: 4px;
    padding: 8px 16px 4px;
    border-top: 1px solid #f2f6fc;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
    white-space: normal;
    list-style: none;
  }

  .ai-menu-warning {
    padding: 6px 16px;
    font-size: 12px;
    color: #f56c6c;
    line-height: 1.5;
    white-space: normal;
    list-style: none;
    border-bottom: 1px solid #f2f6fc;

    i {
      margin-right: 4px;
    }
  }
}
</style>
